/**
 * Contratos de frete — espelho do módulo `ecommerce/shipping` da
 * nest-end-customer-api (`ShippingService`).
 *
 * Fonte ÚNICA destes tipos no front: checkout e PDP consomem daqui. O cálculo
 * de frete é do backend, sempre — o front nunca reimplementa regra de preço
 * (ver a nota "a C2 é a única implementação" no ARCHITECTURE dos repos da nest).
 */

/** Modalidade do método de frete. `melhor_envio` é cotado por transportadora. */
export type ShippingMethodType = "distance" | "zip_range" | "pickup" | "melhor_envio";

/** Opção de frete de um método (uma por método que cobre o CEP). */
export interface ShippingOption {
    methodId: number;
    name: string;
    description: string | null;
    /** `distance` | `zip_range` | `pickup` — string livre no DTO da nest. */
    type: ShippingMethodType | string;
    price: number;
    ruleId: number;
    ruleName: string;
    /** Distância usada (km) quando o método é por distância. */
    distanceKm?: number | null;
    /**
     * Prazo já formatado pelo lojista (ex.: "Em até 2 dias úteis"). `null`
     * quando o método é de agendamento — aí o prazo é a faixa escolhida.
     */
    fixedDeliveryLabel: string | null;
    /** Método com agendamento: data/hora escolhidas no checkout. */
    schedulingEnabled: boolean;
    /**
     * Serviço da transportadora (ex.: PAC). Só em método de transportadora, onde UM método
     * rende várias opções — por isso a opção é identificada por método + serviço, nunca só
     * pelo `methodId`. Precisa voltar ao backend no fechamento.
     */
    carrierServiceId?: number | null;
    /** Transportadora responsável (ex.: Correios). */
    carrierName?: string | null;
}

/**
 * Identidade de uma opção na tela: é o que marca o selecionado, o que reencontra a escolha
 * depois de recotar e o que vira `key` da lista.
 */
export function optionKey(option: Pick<ShippingOption, "methodId" | "carrierServiceId">): string {
    return `${option.methodId}:${option.carrierServiceId ?? ""}`;
}

/** Retorno de /shipping/simulate e /shipping/calculate. */
export interface ShippingSimulation {
    cep: string;
    /** Valor considerado no cálculo — as regras têm faixa de valor. */
    cartValue: number;
    totalWeightKg: number;
    totalVolumeM3: number;
    options: ShippingOption[];
    /** Ex.: CEP fora da base de geolocalização (métodos por distância caem fora). */
    warnings: string[];
}

/** Item avulso cotado por /shipping/calculate (sem passar pelo carrinho). */
export interface ShippingItem {
    productId: number;
    quantity: number;
}

/**
 * Faixa de data/horário de um método agendado (GET /shipping/slots/:methodId).
 * `date` é `YYYY-MM-DD`, horários `HH:mm` — exatamente o que o fechamento
 * devolve à nest em `shippingSlotDate/Start/End`.
 */
export interface AvailableSlot {
    date: string;
    /** 0 = domingo … 6 = sábado. */
    dayOfWeek: number;
    startTime: string;
    endTime: string;
    /** Rótulo pronto do backend (ex.: "Entre 07h e 10h"). */
    label: string;
}

/** Retorno de /shipping/slots/:methodId. */
export interface AvailableSlotsResponse {
    methodId: number;
    storeId: number;
    slots: AvailableSlot[];
}

/**
 * `GET /shipping/free-shipping` — progresso do carrinho rumo ao frete grátis
 * (a barra "faltam R$ X"). Quem decide o que é "frete grátis" é a nest: regra
 * de ENTREGA com taxas zeradas e valor mínimo, ou cupom que zera o frete.
 */
export interface FreeShippingProgress {
    /** A loja tem alguma regra de frete grátis alcançável para este carrinho. */
    eligible: boolean;
    /** O carrinho já atinge a regra (ou tem cupom que zera o frete). */
    achieved: boolean;
    /** Valor mínimo do carrinho; `null` sem regra ou quando é por cupom. */
    threshold: number | null;
    /** Valor do carrinho na base do frete (preço da loja, antes de cupom). */
    cartValue: number;
    /** Quanto falta (0 quando atingido ou sem regra). */
    missing: number;
    /** Fração 0..1 (1 quando atingido). */
    progress: number;
    source: "rule" | "coupon" | "none";
    /** Cupom de frete grátis em jogo: a meta (`applied: false`) ou o já aplicado. */
    coupon: { code: string; name: string; applied: boolean } | null;
    method: { methodId: number; name: string; type: ShippingMethodType | string } | null;
    /** O CEP entrou na conta (cobertura aplicada). */
    cepConsidered: boolean;
}
